import {
  useMergeRefs,
  useFloating,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react"

import classNames from "classnames"
import type {
  Ref,
  ComponentPropsWithoutRef,
} from "react"

import {
  forwardRef,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react"
import { Flipper } from "react-flip-toolkit"

import styles from "./Toast.module.scss"
import { ToasterContext } from "./ToasterContext"
import type { ToastState } from "./ToastsContext"
import { ToastsContext } from "./ToastsContext"

type Props = ComponentPropsWithoutRef<"div">

// this renders the container for the toasts floating at the top of the page
// all toasts that will be rendered inthis container must be children of this element
export const Toaster = forwardRef<HTMLDivElement, Props>(
  function Toaster(props: Props, propRef: Ref<HTMLDivElement>) {
    const {
      children,
      ...htmlDivProps
    } = props

    // state to hold the collection of toasts
    const [
      toasts,
      setToasts,
    ] = useState<Record<string, ToastState>>({})

    const flipKeyRef = useRef<number>(0)

    const flipKey = useMemo(
      () => {
        flipKeyRef.current += 1
        return flipKeyRef.current
      },
      [
        toasts,
        flipKeyRef,
      ],
    )

    const toastsRef = useRef<Record<string, ToastState>>(toasts)

    // function that adds a toastID to the collection of toasts
    const showToast = useCallback(
      (id: string, component: ToastState["component"]) => {
        if (!toastsRef.current[id]) {
          toastsRef.current = {
            ...toastsRef.current,
            [id]: {
              component,
              status: "unmounted",
            },
          }

          setToasts(toastsRef.current)
        }
      },
      [
        toastsRef,
        setToasts,
      ],
    )

    // function that updates the status for a particular toast
    const updateToast = useCallback(
      (id: string, toastStatus: ToastState["status"]) => {
        const currentValue = toastsRef.current[id]

        if (currentValue) {
          toastsRef.current = {
            ...toastsRef.current,
            [id]: {
              ...currentValue,
              status: toastStatus,
            },
          }

          setToasts(toastsRef.current)
        }
      },
      [
        toastsRef,
        setToasts,
      ],
    )

    // function that removes a toastID from the collection of toasts
    const removeToast = useCallback(
      (id: string) => {
        toastsRef.current = { ...toastsRef.current }
        delete toastsRef.current[id]
        setToasts(toastsRef.current)
      },
      [
        toastsRef,
        setToasts,
      ],
    )

    // state to hold the node for holding toasts
    const [
      toasterRoot,
      setToasterRoot,
    ] = useState<HTMLElement | null>(null)

    // ref callback to set the toasterRoot node
    const toasterRootCallback = useCallback(
      (el: HTMLDivElement | null) => {
        setToasterRoot(el)
      },
      [setToasterRoot],
    )

    const toasterContextValue = useMemo(
      () => ({
        toasterRoot,
        showToast,
        updateToast,
        removeToast,
      }),
      [
        toasterRoot,
        showToast,
        updateToast,
        removeToast,
      ],
    )

    const isOpen = Object.entries(toasts).length > 0
    const data = useFloating({ open: isOpen })

    const ref = useMergeRefs([
      propRef,
      toasterRootCallback,
    ])

    const className = classNames(
      props.className,
      styles.toaster,
    )

    return (
      <ToasterContext.Provider value={toasterContextValue}>
        <FloatingPortal>
          {isOpen && (
            <FloatingFocusManager
              context={data.context}
              modal={false}
            >
              <Flipper flipKey={`${flipKey}`}>
                <div
                  {...htmlDivProps}
                  className={className}
                  ref={ref}
                />
              </Flipper>
            </FloatingFocusManager>
          )}
        </FloatingPortal>
        <ToastsContext.Provider value={toasts}>
          {Object.entries(toasts).map(
            ([
              key,
              value,
            ]) => (
              <value.component
                key={key}
                id={key}
              />
            ),
          )}
        </ToastsContext.Provider>
        {children}
      </ToasterContext.Provider>
    )
  },
)
